import type { AuthTokens } from './cookieUtils'

type ApiResponse<T> = {
  data: T
  message: string
  status: number
}

async function bffPost<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorType =
      (
        errorData as {
          error?: { errorDetail?: Array<{ errorType?: string }> }
        }
      ).error?.errorDetail?.[0]?.errorType ?? response.statusText
    throw new ApiError(errorType, response.status)
  }

  const json = (await response.json()) as ApiResponse<T>
  return json.data
}

export class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

// Both passwordless endpoints reject (403) an account that still lives in MB2,
// before any code is sent.
export const MB2_ACCOUNT_EXISTS_ERROR_CODE = 'MB2_ACCOUNT_EXISTS'

export async function initiateLogin(email: string): Promise<void> {
  await bffPost('/auth/login', { email })
}

// The auth service returns the originating system alongside the tokens on a
// successful verify (e.g. 'magento' for accounts migrated from MB2). Used to
// gate non-migrated users back to MB2 login.
export type VerifyResult = AuthTokens & {
  sourceSystem?: string
}

export async function verifyOtp(
  email: string,
  otp: string,
): Promise<VerifyResult> {
  return bffPost<VerifyResult>('/auth/verify', { email, otp })
}

export async function refreshAccessToken(
  refreshToken: string,
): Promise<AuthTokens> {
  return bffPost<AuthTokens>('/auth/refresh', {
    refresh_token: refreshToken,
  })
}

// Renews a materialbank.com session, whose tokens are HttpOnly cookies: the
// auth service reads the refresh token from the cookie and sets new ones.
export async function refreshSessionCookies(): Promise<void> {
  const response = await fetch('/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })

  if (!response.ok) {
    throw new ApiError(response.statusText, response.status)
  }
}

export type SessionPayload = {
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    accountComplete: boolean
  }
  mode: 'cookie' | 'bearer'
  // /auth/me had to renew a lapsed session to answer, so anything fetched
  // before it went out as a guest.
  renewed?: boolean
}

type SessionResponseBody = {
  user: SessionPayload['user'] | null
  mode?: SessionPayload['mode']
  renewed?: boolean
}

export async function fetchSession(): Promise<SessionPayload | null> {
  const response = await fetch('/auth/me', {
    method: 'GET',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  })

  if (!response.ok) {
    return null
  }

  const body = (await response.json()) as SessionResponseBody

  if (!body.user || !body.mode) {
    return null
  }

  return {
    user: body.user,
    mode: body.mode,
    ...(body.renewed === true ? { renewed: true } : {}),
  }
}

export async function bffLogout(): Promise<void> {
  await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'include',
  })
}
